/**
 * Классификация HTTP-ошибок от ChainerExceptionFilter (бэк userbot).
 * Бэк возвращает body.message = код категории:
 *   "session-dead" (401), "flood-wait" (429 + retryAfter),
 *   "telegram-unavailable" (502), либо произвольный текст.
 * См. docs/superpowers/specs/userbot-chainer-frontend-spec-constructor-evolution.md
 */

export type ChainerErrorCategory =
  | "session-dead"
  | "flood-wait"
  | "telegram-unavailable"
  | "unknown"

export interface ChainerError {
  category: ChainerErrorCategory
  message: string
  retryAfter?: number
}

interface RtkQueryError {
  status?: number | string
  data?: { message?: string; retryAfter?: number; error?: string }
  error?: string
  message?: string
}

const SESSION_DEAD_MESSAGE =
  "Сессия недействительна (AUTH_KEY_UNREGISTERED). Импортируйте новую."
const TELEGRAM_UNAVAILABLE_MESSAGE = "Нет связи с Telegram, повторите."

/**
 * Маппит ошибку RTK Query в структурированную ChainerError с понятным сообщением.
 * Приоритет: data.message → status → fallback на getApiErrorMessage-логику.
 */
export function getChainerError(error: unknown): ChainerError {
  if (!error) {
    return { category: "unknown", message: "Неизвестная ошибка" }
  }

  const err = error as RtkQueryError
  const data = err.data
  const msg = data?.message
  const statusNum = typeof err.status === "number" ? err.status : undefined

  // 1. Явный код категории в data.message
  if (msg === "session-dead" || statusNum === 401) {
    return { category: "session-dead", message: SESSION_DEAD_MESSAGE }
  }
  if (msg === "flood-wait" || statusNum === 429) {
    const retryAfter = data?.retryAfter
    const message = retryAfter
      ? `Слишком много запросов, подождите ${retryAfter} сек.`
      : "Слишком много запросов, подождите немного."
    return retryAfter
      ? { category: "flood-wait", message, retryAfter }
      : { category: "flood-wait", message }
  }
  if (msg === "telegram-unavailable" || statusNum === 502) {
    return {
      category: "telegram-unavailable",
      message: TELEGRAM_UNAVAILABLE_MESSAGE,
    }
  }

  // 2. Fallback — прочие ошибки (неверный код, валидация, etc.)
  if (typeof msg === "string") {
    return { category: "unknown", message: msg }
  }
  if (typeof err.error === "string") {
    return { category: "unknown", message: err.error }
  }
  if (typeof err.message === "string") {
    return { category: "unknown", message: err.message }
  }
  if (data?.error) {
    return { category: "unknown", message: data.error }
  }
  return { category: "unknown", message: "Произошла ошибка" }
}
